import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import GavelIcon from "@mui/icons-material/Gavel";
import CloseIcon from "@mui/icons-material/Close";
import { axiosConfig } from "../../axios/axiosConfig";
import OptionsDisclaimerTable from "./OptionsDisclaimerTable";
import toast from "react-hot-toast";

const OptionsDisclaimer = () => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [disclaimers, setDisclaimers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [disclaimerLoading, setDisclaimerLoading] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  // Fetch disclaimers
  const fetchDisclaimers = async () => {
    try {
      setLoading(true);
      const response = await axiosConfig({
        method: "get",
        url: "/disclaimers/",
      });
      setDisclaimers(response.data);
    } catch (error) {
      console.error("Error fetching disclaimers:", error);
      const errMsg = error?.response?.data?.response || error?.message || "Failed to fetch disclaimers"
      toast.error(errMsg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDisclaimers();
  }, []);

  const handleSave = async () => {
    const trimmedInput = inputValue.trim();

    if (!trimmedInput) {
      toast.error("Disclaimer text cannot be blank");
      return;
    }
    if (!isNaN(trimmedInput)) {
      toast.error("Disclaimer text cannot be numeric");
      return;
    }
    try {
      setDisclaimerLoading(true);
      const response = await axiosConfig({
        method: "post",
        url: "/disclaimers/",
        data: {
          disclaimer_text: inputValue,
        },
      });


      toast.success(response?.message || "Disclaimer added successfully");
      setOpen(false);
      setInputValue("");
      fetchDisclaimers();
    } catch (error) {
      console.error("Error saving disclaimer:", error);
      const errMsg =
        error?.response?.data?.response ||
        error?.message ||
        "Failed to add disclaimer";
      toast.error(errMsg);
    } finally {
      setDisclaimerLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, background: "#eef2f7", minHeight: "100vh" }}>
      {/* Page Header */}
      <Box
        sx={{
          background: "#04343a",
          borderRadius: "12px",
          boxShadow: "0 4px 20px rgba(4,52,58,0.3)",
          p: "20px 28px",
          mb: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <GavelIcon sx={{ color: "#fff", fontSize: 32 }} />
          <Box>
            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "20px" }}>
              Options Disclaimer
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: "13px" }}>
              Manage options disclaimer text
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          onClick={handleOpen}
          sx={{
            background: "#016551",
            borderRadius: "8px",
            fontWeight: 600,
            px: 3,
            boxShadow: "0 4px 12px rgba(1,101,81,0.3)",
            "&:hover": {
              background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)",
              boxShadow: "0 6px 16px rgba(1,101,81,0.4)",
            },
          }}
        >
          Add Disclaimer
        </Button>
      </Box>

      {/* Table Card */}
      <Box
        sx={{
          background: "#fff",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
          overflow: "hidden",
        }}
      >
        <OptionsDisclaimerTable
          disclaimers={disclaimers}
          loading={loading}
          fetchDisclaimers={fetchDisclaimers}
        />
      </Box>

      {/* Add Disclaimer Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: "12px", position: "relative" } }}
      >
        <DialogTitle
          sx={{ background: "#04343a", color: "#fff", fontWeight: 700, fontSize: "18px", pb: 2 }}
        >
          Add Disclaimer
        </DialogTitle>
        <IconButton
          onClick={handleClose}
          sx={{ position: "absolute", right: 8, top: 8, color: "#fff" }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ pt: 3 }}>
          <TextField
            autoFocus
            margin="dense"
            label="Disclaimer Text"
            type="text"
            fullWidth
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} sx={{ color: "#64748b", fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={disclaimerLoading}
            sx={{
              background: "linear-gradient(135deg, #016551, #014d3d)",
              borderRadius: "8px",
              fontWeight: 600,
              px: 4,
              "&:hover": { background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)" },
              "&:disabled": { background: "#ccc" },
            }}
          >
            {disclaimerLoading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OptionsDisclaimer;
